import { useState, useContext } from "react";
import { FormattedMessage } from "react-intl";
import { useOutletContext } from "react-router-dom";

import RouterLink from "../../components/link/RouterLink";
import Navbar from "../../components/navbar/navbar";
import Footer from "../../components/footer/footer";
import Pagination from "../../components/pagination/Pagination";
import HelmetSEO from "../../components/helmetSeo/helmetSeo";
import RichTextWithBlokRenderer from "../../components/richTextRenderer/richTextRenderer";
import OneColumnArticleListingLayout from "../../components/articleListingLayouts/oneColumnArticleListingLayout/oneColumnArticleListingLayout";

import GetComponentsFromBloks from "../../helpers/bloksRenderer";
import { TagResultsTemplateStoryToObject } from "../../helpers/storyHelpers";
import { useCurrentPageSearchParam } from "../../hooks/usePagination";
import {
  storyblokStoryUuidToUrl,
  tagNameFromPath,
} from "../../helpers/URLHelpers";

import styles from "./tagResultsTemplate.module.scss";
import HeaderBannerPanel from "../../bloks/panels/headerBannerPanel/headerBannerPanel";
import LowerBannerPanel from "../../bloks/panels/lowerBannerPanel/lowerBannerPanel";
import ListingHeader from "../../components/listingHeader/listingHeader";
import { useTagArticles } from "../../hooks/useTagArticles";
import PageContentWrapper from "../../components/PageContentWrapper";

const PER_PAGE = 10,
  FEATURED = 3;

function TagResultsTemplate() {
  const { story } = useOutletContext();

  const pageData = TagResultsTemplateStoryToObject(story);
  const currentPageSearchParam = useCurrentPageSearchParam();
  const tagName = tagNameFromPath();
  const decodedTagName = decodeURIComponent(tagName);
  const pagePage = storyblokStoryUuidToUrl(story.uuid);

  const [currentPage, setCurrentPage] = useState(currentPageSearchParam || 1);
  const [currentArticles, totalArticles, isLoading] = useTagArticles(
    tagName,
    PER_PAGE,
    currentPage
  );

  function handlePageChange(newPage) {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
      const url = new URL(window.location);
      if (newPage === 1) {
        url.searchParams.delete("page");
      } else {
        url.searchParams.set("page", newPage);
      }
      window.history.pushState({}, "", url);
      window.scrollTo(0, 0);
    }
  }

  let asideComponentList = [];
  if (story && story.content && story.content.aside) {
    asideComponentList = GetComponentsFromBloks(story.content.aside, story);
  }

  let beforeAdArticles = [],
    afterAdArticles = [];

  if (currentArticles.length > FEATURED) {
    beforeAdArticles = currentArticles.slice(0, FEATURED);
    afterAdArticles = currentArticles.slice(FEATURED);
  } else {
    beforeAdArticles = currentArticles;
  }
  return (
    <>
      <HelmetSEO
        lang={story.lang}
        page_path={pagePage}
        story_alternates={pageData ? pageData.alternates : []}
        seo_data={pageData ? pageData.seo : {}}
        fallback_title={pageData && pageData.headline ? pageData.headline : ""}
      />
      <Navbar story_language={story.lang} />
      <PageContentWrapper>
        <div className={`view ${styles.tagResultsPage}`}>
          <HeaderBannerPanel header_banner_panel={story?.content?.ads?.header_banner_panel} />
          <div className="page-content">
            <div className={styles.breadcrumbs}>
              <RouterLink to="/">
                <FormattedMessage id="page.tagresultspage.breadcrumbs.crunchyroll" />
              </RouterLink>
              <span>{decodedTagName}</span>
            </div>
            <div className={styles.layout}>
              <main>
                <ListingHeader
                  withClass={styles.mainListingHeader}
                  underlineColor={"backgroundcolor-crscooter"}
                >
                  <h1>{`${pageData.headline} "${decodedTagName}"`}</h1>
                </ListingHeader>
                {isLoading ? <div className="loader centered" /> : null}
                {currentArticles.length <= 0 && !isLoading ? (
                  <div className={styles.noResults}>
                    <RichTextWithBlokRenderer
                      document={pageData.no_results_message}
                    />
                  </div>
                ) : null}
                {beforeAdArticles.length > 0 ? (
                  <OneColumnArticleListingLayout articles={beforeAdArticles} />
                ) : null}
                {pageData.show_ad_panel ? (
                  <LowerBannerPanel blok={{margin_top: "default", margin_bottom: "default"}} lower_banner_panel={story?.content?.ads?.lower_banner_panel} />
                ) : null}
                {afterAdArticles.length > 0 ? (
                  <OneColumnArticleListingLayout articles={afterAdArticles} />
                ) : null}
                {totalArticles > PER_PAGE ? (
                  <Pagination
                    currentPage={currentPage}
                    totalCount={totalArticles}
                    pageSize={PER_PAGE}
                    onPageChange={handlePageChange}
                  />
                ) : null}
              </main>
              <aside>
                <div className={styles.asideContent}>{asideComponentList}</div>
              </aside>
            </div>
          </div>
        </div>
      </PageContentWrapper>
      <Footer />
    </>
  );
}

export default TagResultsTemplate;
